import { Smartphone } from 'lucide-react';
import React from 'react';
import { type ReleaseChannelRow } from '../api';
import {
  StoreReviewControl,
  reviewStatusCopy,
  reviewStatusTone,
  type StoreReviewStatus
} from '../components/StoreReviewControl';
import { StatusChip } from '../components/Avatar';
import { EmptyState } from '../components/EmptyState';
import { PageStat } from '../components/PageStat';
import { SectionCard } from '../components/SectionCard';
import { useSession } from '../contexts/SessionContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../hooks/useActionDialog';
import { toRuntimeConfigValueDrafts } from '../utils/runtimeConfigValueValidation';
import {
  bumpVersion,
  formatChannelLabel,
  formatRelativeDate
} from '../utils/formatters';

type AppReleasesPageProps = {
  openDialog: UseActionDialogReturn['openDialog'];
};

function AppReleasesPage({ openDialog }: AppReleasesPageProps) {
  const { session } = useSession();
  const canManage = session?.user?.role !== 'viewer';
  const {
    workspace,
    activeOrganization,
    activeProject,
    activeReleaseAppId,
    activeReleaseAppVersionId,
    releaseAppVersionById,
    channels,
    visibleEnvironments,
    isEnvironmentVisible,
    setActiveReleaseAppId,
    setActiveReleaseAppVersionId,
    refreshWorkspace
  } = useWorkspace();

  const { releaseApps, releaseAppVersions, runtimeConfigs } = workspace;
  const visibleRuntimeConfigs = runtimeConfigs.filter((config) =>
    isEnvironmentVisible(config.environment)
  );
  const activeRuntimeConfigs = visibleRuntimeConfigs.filter(
    (config) => config.isActive
  );
  const configuredVersionIds = new Set(
    activeRuntimeConfigs
      .map((config) => config.releaseAppVersionId)
      .filter((id): id is string => Boolean(id))
  );
  const selectedReleaseApp =
    releaseApps.find((app) => app.releaseAppId === activeReleaseAppId) ??
    releaseApps[0] ??
    null;
  const selectedAppVersions = selectedReleaseApp
    ? releaseAppVersions.filter(
        (version) => version.releaseAppId === selectedReleaseApp.releaseAppId
      )
    : [];
  const selectedVersion =
    selectedAppVersions.find(
      (version) => version.releaseAppVersionId === activeReleaseAppVersionId
    ) ??
    selectedAppVersions[0] ??
    null;
  const selectedVersionTargets = selectedVersion
    ? channels.filter(
        (channel) =>
          channel.releaseAppVersionId === selectedVersion.releaseAppVersionId
      )
    : [];
  const selectedVersionConfigs = selectedVersion
    ? runtimeConfigs.filter(
        (config) =>
          config.releaseAppVersionId === selectedVersion.releaseAppVersionId
      )
    : [];
  const registryRows = releaseApps.flatMap((releaseApp) => {
    const versions = releaseAppVersions.filter(
      (version) => version.releaseAppId === releaseApp.releaseAppId
    );
    return versions.length > 0
      ? versions.map((version) => ({ releaseApp, version }))
      : [{ releaseApp, version: null }];
  });

  function selectVersion(releaseAppId: string, releaseAppVersionId: string) {
    setActiveReleaseAppId(releaseAppId);
    setActiveReleaseAppVersionId(releaseAppVersionId);
  }

  function handleCreateReleaseApp() {
    if (!activeOrganization || !activeProject) return;
    openDialog({
      kind: 'release-app',
      organizationId: activeOrganization.id,
      projectId: activeProject.id,
      platform: 'ios',
      name: `${activeProject.name} iOS`,
      environment: ''
    });
  }

  function openVersionDialog(
    releaseApp: (typeof releaseApps)[number],
    sourceVersion: (typeof releaseAppVersions)[number] | null
  ) {
    openDialog({
      kind: 'release-app-version',
      organizationId: releaseApp.organizationId,
      projectId: releaseApp.projectId,
      releaseAppId: releaseApp.releaseAppId,
      version: sourceVersion ? bumpVersion(sourceVersion.version) : '1.0.0',
      sourceReleaseAppVersionId: sourceVersion?.releaseAppVersionId || ''
    });
  }

  function handleCreateReleaseAppVersion() {
    if (!selectedReleaseApp) return;
    openVersionDialog(selectedReleaseApp, selectedVersion);
  }

  function handleEditChannel(channel: ReleaseChannelRow) {
    openDialog({
      kind: 'channel',
      channelId: channel.releaseChannelId,
      organizationId: channel.organizationId,
      projectId: channel.projectId || activeProject?.id || '',
      tenantEnvironmentId: channel.tenantEnvironmentId || '',
      environment: channel.environment,
      lookupEnvironment: channel.environment,
      channel: channel.channel,
      lookupChannel: channel.channel,
      bundleCdnHostOverride: channel.bundleCdnHostOverride || '',
      isActive: channel.isActive
    });
  }

  function handleEditRuntimeConfig(
    config: (typeof visibleRuntimeConfigs)[number]
  ) {
    const resolvedVersion =
      releaseAppVersionById.get(config.releaseAppVersionId || '') ||
      selectedVersion;
    openDialog({
      kind: 'runtime-config',
      runtimeConfigId: config.runtimeConfigId,
      name: config.name,
      hostname: config.hostname || '',
      releaseAppVersionId:
        config.releaseAppVersionId ||
        resolvedVersion?.releaseAppVersionId ||
        '',
      nativeAppId: config.nativeAppId || '',
      platform:
        config.platform === 'ios' || config.platform === 'android'
          ? config.platform
          : 'web',
      nativeVersionRange: config.nativeVersionRange || '',
      environment: config.environment,
      expressApiHostname: config.expressApiHostname,
      staticCdnPrefix: config.staticCdnPrefix || '',
      analyticsEnabled: config.analyticsEnabled,
      analyticsPageviewsEnabled: config.analyticsPageviewsEnabled,
      posthogApiKey: config.posthogApiKey || '',
      posthogHost: config.posthogHost || '',
      otelEnabled: config.otelEnabled,
      otelCollectorUrl: config.otelCollectorUrl || '',
      priority: `${config.priority}`,
      isActive: config.isActive,
      values: toRuntimeConfigValueDrafts(config.values)
    });
  }

  function handleCreateRuntimeConfig() {
    if (!activeProject || !selectedVersion) return;
    const environment =
      (visibleEnvironments[0]?.slug as
        | 'dev'
        | 'staging'
        | 'production'
        | 'internal') || 'dev';
    openDialog({
      kind: 'runtime-config',
      name: `${activeProject.name} ${selectedVersion.version} runtime`,
      hostname: '',
      releaseAppVersionId: selectedVersion.releaseAppVersionId,
      nativeAppId: '',
      platform: 'web',
      nativeVersionRange: '',
      environment,
      expressApiHostname: '',
      staticCdnPrefix: '',
      analyticsEnabled: false,
      analyticsPageviewsEnabled: false,
      posthogApiKey: '',
      posthogHost: '',
      otelEnabled: false,
      otelCollectorUrl: '',
      priority: '100',
      isActive: true,
      values: []
    });
  }

  return (
    <>
      {releaseApps.length > 0 ? (
        <section className="metricsGrid">
          <PageStat
            label="Platform tracks"
            value={`${releaseApps.length}`}
            note="iOS and Android delivery tracks."
          />
          <PageStat
            label="Store versions"
            value={`${releaseAppVersions.length}`}
            note="Versions with a release target."
          />
          <PageStat
            label="Runtime ready"
            value={`${configuredVersionIds.size}`}
            note="Versions with an active runtime rule."
          />
          <PageStat
            label="Needs runtime"
            value={`${releaseAppVersions.length - configuredVersionIds.size}`}
            note="Versions not addressable at runtime."
          />
        </section>
      ) : null}

      <SectionCard
        className="appVersionsRegistry"
        label="App store versions"
        title="Platform and store versions"
        description="Each row is one store version and its generated delivery state."
        action={
          <div className="buttonRow">
            <button
              type="button"
              className="panelControl"
              onClick={handleCreateReleaseApp}
            >
              New platform
            </button>
            <button
              type="button"
              className="panelControl"
              onClick={handleCreateReleaseAppVersion}
              disabled={!selectedReleaseApp}
            >
              New version
            </button>
          </div>
        }
      >
        {registryRows.length > 0 ? (
          <div
            className="releaseTableWrap"
            role="region"
            aria-label="App store versions table"
            tabIndex={0}
          >
            <table className="releaseTable appVersionsTable">
              <thead>
                <tr>
                  <th>Platform track</th>
                  <th>Store version</th>
                  <th>Targets</th>
                  <th>Runtime</th>
                  <th>Store review</th>
                  <th>Created</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {registryRows.map(({ releaseApp, version }) => {
                  const targets = version
                    ? channels.filter(
                        (channel) =>
                          channel.releaseAppVersionId ===
                          version.releaseAppVersionId
                      )
                    : [];
                  const configs = version
                    ? activeRuntimeConfigs.filter(
                        (config) =>
                          config.releaseAppVersionId ===
                          version.releaseAppVersionId
                      )
                    : [];
                  const selected =
                    version?.releaseAppVersionId ===
                    selectedVersion?.releaseAppVersionId;
                  return (
                    <tr
                      key={
                        version?.releaseAppVersionId || releaseApp.releaseAppId
                      }
                      className={selected ? 'isSelectedRegistryRow' : undefined}
                    >
                      <td>
                        <div className="releaseIdentity">
                          <strong>{releaseApp.name}</strong>
                          <span>
                            {releaseApp.platform.toUpperCase()} ·{' '}
                            {releaseApp.slug}
                          </span>
                        </div>
                      </td>
                      <td>
                        {version ? (
                          <button
                            type="button"
                            className="registryTextButton"
                            onClick={() =>
                              selectVersion(
                                releaseApp.releaseAppId,
                                version.releaseAppVersionId
                              )
                            }
                          >
                            {version.version}
                          </button>
                        ) : (
                          <span className="registryEmptyValue">
                            No version registered
                          </span>
                        )}
                      </td>
                      <td>
                        <strong className="registryCount">
                          {targets.length}
                        </strong>
                      </td>
                      <td>
                        <StatusChip
                          tone={configs.length > 0 ? 'success' : 'warn'}
                        >
                          {configs.length > 0
                            ? `${configs.length} active`
                            : 'Missing'}
                        </StatusChip>
                      </td>
                      <td>
                        {version ? (
                          <StatusChip
                            tone={reviewStatusTone(
                              (version.storeReviewStatus as
                                | StoreReviewStatus
                                | undefined) || 'not_submitted'
                            )}
                          >
                            {
                              reviewStatusCopy[
                                (version.storeReviewStatus as
                                  | StoreReviewStatus
                                  | undefined) || 'not_submitted'
                              ]
                            }
                          </StatusChip>
                        ) : (
                          <span className="registryEmptyValue">—</span>
                        )}
                      </td>
                      <td>
                        {formatRelativeDate(
                          version?.created || releaseApp.created
                        )}
                      </td>
                      <td className="releaseTableAction">
                        <button
                          type="button"
                          className="secondaryButton"
                          onClick={() => {
                            if (version) {
                              selectVersion(
                                releaseApp.releaseAppId,
                                version.releaseAppVersionId
                              );
                            } else {
                              openVersionDialog(releaseApp, null);
                            }
                          }}
                        >
                          {version
                            ? selected
                              ? 'Selected'
                              : 'Open'
                            : 'Add version'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={<Smartphone size={20} />}
            title="No platform tracks yet"
            description="Add an iOS or Android platform to begin registering store versions."
            action={
              <button
                type="button"
                className="secondaryButton"
                onClick={handleCreateReleaseApp}
              >
                Add platform
              </button>
            }
          />
        )}
      </SectionCard>

      {selectedVersion && selectedReleaseApp ? (
        <StoreReviewControl
          key={`${selectedVersion.releaseAppVersionId}:${selectedVersion.storeReviewUpdatedAt || ''}`}
          app={selectedReleaseApp}
          version={selectedVersion}
          configs={runtimeConfigs}
          token={session?.accessToken}
          canManage={canManage}
          onSaved={refreshWorkspace}
        />
      ) : null}

      <SectionCard
        className="appVersionInspector"
        label="Selected version"
        title={
          selectedVersion && selectedReleaseApp
            ? `${selectedReleaseApp.name} ${selectedVersion.version}`
            : 'Choose a store version'
        }
        description="Runtime rules and release targets belonging to exactly this app version."
        action={
          selectedVersion && selectedReleaseApp ? (
            <div className="buttonRow">
              <button
                type="button"
                className="panelControl"
                onClick={() =>
                  openVersionDialog(selectedReleaseApp, selectedVersion)
                }
              >
                Create next version
              </button>
              <button
                type="button"
                className="panelControl"
                onClick={handleCreateRuntimeConfig}
              >
                Add runtime rule
              </button>
            </div>
          ) : undefined
        }
      >
        {selectedVersion && selectedReleaseApp ? (
          <>
            <div className="versionInspectorSummary">
              <div>
                <span>Platform</span>
                <strong>{selectedReleaseApp.platform.toUpperCase()}</strong>
              </div>
              <div>
                <span>Store version</span>
                <strong>{selectedVersion.version}</strong>
              </div>
              <div>
                <span>Release targets</span>
                <strong>{selectedVersionTargets.length}</strong>
              </div>
              <div>
                <span>Runtime rules</span>
                <strong>{selectedVersionConfigs.length}</strong>
              </div>
            </div>
            <div className="versionInspectorGrid">
              <section>
                <div className="versionInspectorHeading">
                  <strong>Runtime rules</strong>
                  <span>Client matching and endpoints</span>
                </div>
                <div className="versionInspectorRows">
                  {selectedVersionConfigs.length > 0 ? (
                    selectedVersionConfigs.map((config) => (
                      <button
                        key={config.runtimeConfigId}
                        type="button"
                        onClick={() => handleEditRuntimeConfig(config)}
                      >
                        <span>
                          <strong>{config.name}</strong>
                          <small>{config.expressApiHostname}</small>
                        </span>
                        <StatusChip
                          tone={config.isActive ? 'success' : 'neutral'}
                        >
                          {config.environment}
                        </StatusChip>
                      </button>
                    ))
                  ) : (
                    <span className="versionInspectorEmpty">
                      No visible runtime rule.
                    </span>
                  )}
                </div>
              </section>
              <section>
                <div className="versionInspectorHeading">
                  <strong>Release targets</strong>
                  <span>Environment-specific delivery</span>
                </div>
                <div className="versionInspectorRows">
                  {selectedVersionTargets.length > 0 ? (
                    selectedVersionTargets.map((channel) => (
                      <button
                        key={channel.releaseChannelId}
                        type="button"
                        onClick={() => handleEditChannel(channel)}
                      >
                        <span>
                          <strong>{formatChannelLabel(channel)}</strong>
                          <small>
                            {channel.releaseArtifactId
                              ? 'Bundle assigned'
                              : 'Awaiting bundle'}
                          </small>
                        </span>
                        <StatusChip
                          tone={channel.isActive ? 'success' : 'neutral'}
                        >
                          {channel.isActive ? 'Active' : 'Paused'}
                        </StatusChip>
                      </button>
                    ))
                  ) : (
                    <span className="versionInspectorEmpty">
                      No targets generated.
                    </span>
                  )}
                </div>
              </section>
            </div>
          </>
        ) : (
          <EmptyState
            compact
            icon={<Smartphone size={18} />}
            title="No version selected"
            description="Open a version in the registry to inspect its runtime and delivery state."
          />
        )}
      </SectionCard>
    </>
  );
}

export { AppReleasesPage };
